<script setup lang="ts">
import { computed, ref, watch } from "vue";
import "@maersk-global/mds-components-core/mc-label";
import "@maersk-global/mds-components-core/mc-number-stepper";
import { useI18n } from "vue-i18n";
import { useApplyFilter } from "@/features/filters/composables/useApplyFilter/useApplyFilter";
import { useFiltersStore } from "@/store";
import type { FilterDefinition, FilterType } from "@/features/filters/types";
import { FilterTypeEnum } from "@/features/filters/types";

interface Props {
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}

const props = defineProps<Props>();
const { t } = useI18n();
const filtersStore = useFiltersStore();

const { filtersApplied, panelSelectedFilters } = filtersStore;

const zone = props.zonePrefix ?? "default";
const filterKey = props.filter.key;

const getInitialValue = (): number => {
  const { value, min } = props.filter;
  let initialValue: number;
  if (typeof value === "number" || typeof value === "string") {
    initialValue = Number(value);
  } else if (typeof min === "number" || typeof min === "string") {
    initialValue = Number(min);
  } else {
    initialValue = 0;
  }
  return initialValue;
};

const currentValue = ref<number>(getInitialValue());

const panelStoreKey = props.filter.storeZoneSuffix ? `${zone}${props.filter.storeZoneSuffix}` : zone;

const { applyFilter } = useApplyFilter(props.filterType, panelStoreKey);

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return source?.[panelStoreKey]?.[filterKey];
});

/**
 * Helper to get numeric value from filter store
 */
const getFilterValue = (key: string): number | undefined => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const filterValue = source?.[panelStoreKey]?.[key];

  if (filterValue === undefined || filterValue === null) return undefined;

  // Handle nested object structure
  if (typeof filterValue === "object" && filterValue.value !== undefined) {
    const rawValue =
      typeof filterValue.value === "object" && filterValue.value !== null ? filterValue.value.value : filterValue.value;
    return Number(rawValue);
  }

  return Number(filterValue);
};

/**
 * Dynamically compute min value based on validation rules
 * For bucket filters, min is the value of the previous stepper
 */
const dynamicMin = computed(() => {
  const ascendingKey = (props.filter as any).validationRules?.descending;
  if (ascendingKey) {
    const prevValue = getFilterValue(ascendingKey);
    if (prevValue !== undefined && !Number.isNaN(prevValue)) {
      return prevValue;
    }
  }
  return Number(props.filter.min ?? 0);
});

/**
 * Dynamically compute max value based on validation rules
 * For bucket filters, max is the value of the next stepper
 */
const dynamicMax = computed(() => {
  const descendingKey = (props.filter as any).validationRules?.ascending;
  if (descendingKey) {
    const nextValue = getFilterValue(descendingKey);
    if (nextValue !== undefined && !Number.isNaN(nextValue)) {
      return nextValue;
    }
  }
  return Number(props.filter.max ?? 100);
});

const handleChange = (e: any) => {
  currentValue.value = e.target.value;

  const payload = {
    label: e.target.label,
    value: e.target.value,
    filterLabel: e.target.label
  };

  applyFilter(props.filter.key, payload);
};

watch(
  () => selectedFilter.value,
  (newValue) => {
    if (newValue?.value !== undefined) {
      // Extract the raw numeric value, handling nested object structures
      const rawValue =
        typeof newValue.value === "object" && newValue.value !== null ? newValue.value.value : newValue.value;
      currentValue.value = Number(rawValue);
    } else if (typeof newValue === "number" || typeof newValue === "string") {
      currentValue.value = Number(newValue);
    }
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="number-stepper-filter" :data-test="`number-stepper-filter/${filter.key}`">
    <mc-number-stepper
      fit="small"
      :value="currentValue"
      :suffix="filter.suffix || ''"
      :label="t(filter.label ?? '')"
      :min="dynamicMin"
      :hiddenlabel="filter.hiddenlabel || false"
      :max="dynamicMax"
      :step="filter.step || 1"
      class="number-stepper-filter__input"
      :data-test="`number-stepper-filter/${filter.key}/stepper`"
      @input="(e: CustomEvent) => handleChange(e)"
    />

    <p
      v-if="filter.description"
      class="number-stepper-filter__description mds-text--x-small"
      :data-test="`number-stepper-filter/${filter.key}/description`"
    >
      {{ t(filter.description) }}
    </p>
  </div>
</template>

<style scoped lang="scss">
@import url("./NumberStepperFilter.scss");
</style>
